import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import ModalContent from '@components/Modals/components/ModalContent';
import { useAddGameContext } from '../context/AddGameProvider';
import useTrackEvent from '@hooks/useTrackEvent';
import Playstation from '@components/UI/Icon/Icons/Playstation';
import useGameProfiles from '@hooks/API/useGameProfiles';
import { useEffect, useMemo, useState } from 'react';
import { useUserState } from '@context/UserContext';
import XboxOne from '@components/UI/Icon/Icons/XboxOne';
import ToggleTile from '@components/UI/ToggleTile';
import BoxButton from '@components/Buttons/BoxButton';
import { styled } from '@mui/material';
import Flex from '@components/Layout/Flex';
import ElectronicartsText from '@components/UI/Icon/Icons/ElectronicartsText';
import SteamPlain from '@components/UI/Icon/Icons/SteamPlain';
import Skeleton from 'react-loading-skeleton';
import { captureMessage } from '@sentry/react';
const IconBackground = styled('p') `
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  border-radius: 50%;
  border: none;
  background-color: ${({ theme, platformSlug }) => platformSlug ? theme.palette[platformSlug].main : theme.palette.common.black};

  svg {
    fill: ${({ theme }) => theme.palette.common.white};
    color: ${({ theme }) => theme.palette.common.white};
    width: 24px;
    height: 24px;
  }
`;
const StyledToggleTile = styled(ToggleTile) `
  border: 0;
`;
const PersonaName = styled('p') `
  font-size: 16px;
  margin: 0;
  font-weight: 600;
`;
const PersonaPlatformLabel = styled('p') `
  font-size: 12px;
  color: #5d6b82;
  margin: 0;
  font-weight: 500;
`;
const RadioWrapper = styled('div') `
  /* spans are skeleton loaders */
  > span {
    display: block;
  }

  > label,
  > span {
    &:not(:last-of-type) {
      margin-bottom: 8px;
      border-bottom: 1px solid #ebedf0;
    }
  }

  > label:last-of-type,
  > span:last-of-type {
    margin-bottom: 24px;
  }
`;
// Map platform values from API to display information
const platformIconMap = {
    ea: {
        label: 'EA',
        icon: (_jsx(IconBackground, { children: _jsx(ElectronicartsText, {}) })),
    },
    steam: {
        label: 'Steam',
        icon: (_jsx(IconBackground, { children: _jsx(SteamPlain, {}) })),
    },
    psn: {
        label: 'PlayStation Network',
        icon: (_jsx(IconBackground, { platformSlug: "psn", children: _jsx(Playstation, {}) })),
    },
    xbox: {
        label: 'Xbox',
        icon: (_jsx(IconBackground, { platformSlug: "xbox", children: _jsx(XboxOne, {}) })),
    },
};
const PersonaPlatformSelection = ({ className, onContinue, stepName }) => {
    const trackEvent = useTrackEvent();
    const { selectedGame } = useAddGameContext();
    const { userGames, userProfile } = useUserState();
    const [selectedProfile, setSelectedProfile] = useState();
    // Get the user game region ID for the selected game (battlefield-6)
    const userGameRegionId = useMemo(() => {
        var _a, _b, _c;
        if (!(selectedGame === null || selectedGame === void 0 ? void 0 : selectedGame.gameKey) || !userGames)
            return null;
        const gameRegions = (_a = userGames[selectedGame.gameKey]) === null || _a === void 0 ? void 0 : _a.regions;
        // Get the first region's ID (you may need to adjust this logic based on requirements)
        return (_c = (_b = gameRegions === null || gameRegions === void 0 ? void 0 : gameRegions[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : null;
    }, [selectedGame, userGames]);
    // Fetch game profiles from API
    const { gameProfiles, gameProfile, updateGameProfile, isLoadingGameProfiles, isLoadingGameProfile, isMutating: isUpdatingProfile, } = useGameProfiles({ ugrId: userGameRegionId, onMutationSuccess: onContinue });
    useEffect(() => {
        if (gameProfile) {
            const { platformType } = gameProfile;
            setSelectedProfile({
                platform: platformType,
                username: gameProfile.username,
                playerProfileId: gameProfile.playerProfileId,
            });
        }
    }, [gameProfile]);
    const handleSelect = (event) => {
        if (!(gameProfiles === null || gameProfiles === void 0 ? void 0 : gameProfiles.length))
            return;
        const selected = event.currentTarget.value;
        const position = gameProfiles.findIndex((p) => p.platform === selected);
        const selectedChoice = gameProfiles[position];
        if (!selectedChoice)
            return;
        trackEvent({
            type: 'game_linking',
            data: {
                eventName: 'game linking',
                label: `${selectedChoice.platform} - ${selectedChoice.username}`,
                moduleName: 'Button',
                position: position,
            },
        });
        setSelectedProfile(selectedChoice);
    };
    const handleSubmit = () => {
        if (!(gameProfiles === null || gameProfiles === void 0 ? void 0 : gameProfiles.length))
            return;
        const position = gameProfiles.findIndex((p) => p.platform === (selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.platform));
        if (position === -1)
            return;
        if (selectedProfile && userGameRegionId) {
            trackEvent({
                type: 'game_linking',
                data: {
                    eventName: 'game linking',
                    label: `${selectedProfile.platform} - ${selectedProfile.username}`,
                    moduleName: 'Button',
                    position: position,
                },
            });
            updateGameProfile({ ugrId: userGameRegionId, profileData: selectedProfile });
        }
    };
    // This should never happen, the add game flow should throw an error before this step if no profiles exist
    if (!isLoadingGameProfiles && (gameProfiles === null || gameProfiles === void 0 ? void 0 : gameProfiles.length) === 0) {
        captureMessage(`Failed to get gameProfiles.`, {
            extra: { request: 'getGameProfiles', handle: userProfile === null || userProfile === void 0 ? void 0 : userProfile.handle },
        });
    }
    // If there is exactly one game profile, automatically continue
    if (!isLoadingGameProfile && !isLoadingGameProfiles && (gameProfiles === null || gameProfiles === void 0 ? void 0 : gameProfiles.length) === 1) {
        onContinue();
    }
    return (_jsxs(ModalContent, { className: className, stepName: stepName, verticalAlign: "top", children: [_jsx("p", { id: "add-game-persona-platform-selection", children: "Select the account you want to connect" }), _jsx(RadioWrapper, { children: isLoadingGameProfiles || isLoadingGameProfile ? (_jsxs(_Fragment, { children: [_jsx(Skeleton, { height: 72, className: "mb-2", borderRadius: 8 }), _jsx(Skeleton, { height: 72, className: "mb-2", borderRadius: 8 })] })) : (gameProfiles === null || gameProfiles === void 0 ? void 0 : gameProfiles.map((choice) => {
                    var _a, _b;
                    return (_jsx(StyledToggleTile, { name: choice.platform, onChange: handleSelect, selected: choice.platform === (selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.platform), className: "mb-2", value: choice.platform, children: _jsxs(Flex, { gap: 2, children: [(_a = platformIconMap[choice.platform]) === null || _a === void 0 ? void 0 : _a.icon, _jsxs(Flex, { flexDirection: "column", justify: "center", align: "start", gap: 0, children: [_jsx(PersonaName, { children: choice.username }), _jsx(PersonaPlatformLabel, { children: (_b = platformIconMap[choice.platform]) === null || _b === void 0 ? void 0 : _b.label })] })] }) }, choice.platform));
                })) }), _jsx(BoxButton, { type: "button", onClick: handleSubmit, size: "md", variant: "battlefield-6", disabled: !selectedProfile || isUpdatingProfile || isLoadingGameProfiles, children: "Continue" })] }));
};
export default PersonaPlatformSelection;
